import type { TFilters } from 'src/providers/FiltersProvider';

import dayjs from 'dayjs';
import { useCallback } from 'react';

import { Box, Button, Typography } from '@mui/material';

import { ADMIN_TIME_ZONE, getDateInTimeZone } from 'src/utils/time-zone';

export type TQuickFilterId =
  | 'last3Years'
  | 'last2Years'
  | 'lastYear'
  | 'last6Months'
  | 'last3Months'
  | 'lastMonth'
  | 'thisMonth'
  | 'last7Days'
  | string;

type TQuickFilter = {
  id: TQuickFilterId;
  name: string;
  properties: Partial<TFilters>;
};

export const DEFAULT_QUICK_FILTER = 'lastYear';
const adminToday = getDateInTimeZone(new Date(), ADMIN_TIME_ZONE);

export const defaultQuickFilters: TQuickFilter[] = [
  {
    id: 'last2Years',
    name: '2023',
    properties: {
      dateFrom: dayjs('2023-01-01').startOf('year').format('YYYY-MM-DD'),
      dateTo: dayjs('2023-12-31').endOf('year').format('YYYY-MM-DD'),
    },
  },
  {
    id: 'lastYear',
    name: 'Last year',
    properties: {
      dateFrom: adminToday.subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
      dateTo: adminToday.subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
    },
  },
  {
    id: 'last6Months',
    name: 'Last 6 months',
    properties: {
      dateFrom: adminToday.subtract(6, 'month').startOf('month').format('YYYY-MM-DD'),
      dateTo: adminToday.startOf('month').format('YYYY-MM-DD'),
    },
  },
  {
    id: 'last3Months',
    name: 'Last 3 months',
    properties: {
      dateFrom: adminToday.subtract(3, 'month').startOf('month').format('YYYY-MM-DD'),
      dateTo: adminToday.startOf('month').format('YYYY-MM-DD'),
    },
  },
  {
    id: 'yearToDate',
    name: 'Year to date',
    properties: {
      dateFrom: adminToday.startOf('year').format('YYYY-MM-DD'),
      dateTo: adminToday.format('YYYY-MM-DD'),
    },
  },
];

export type QuickFiltersProps = {
  label?: string;
  quickFilters?: TQuickFilter[];
  onUpdateFilters: (filters: Partial<TFilters>) => void;
};

export function QuickFilters({
  label = 'Quick filters:',
  quickFilters = defaultQuickFilters,
  onUpdateFilters,
}: QuickFiltersProps) {
  const onQuickFilter = useCallback(
    (filter: TQuickFilter) => {
      onUpdateFilters(filter.properties);
    },
    [onUpdateFilters]
  );

  return (
    <Box
      sx={{
        gap: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <Typography variant="subtitle2">{label}</Typography>
      {quickFilters.map((filter) => (
        <Button key={filter.name} onClick={() => onQuickFilter(filter)} variant="soft">
          {filter.name}
        </Button>
      ))}
    </Box>
  );
}
